import axios from "axios";
import React from "react";
import { FormattedMessage } from "react-intl";
import { toast } from "react-toastify";
import { isGuestAppUrl } from "../helpers/guestApp";
import { getToken } from "../helpers/session";

axios.defaults.baseURL = process.env.REACT_APP_API2_EP;
let apiEndpoint = process.env.REACT_APP_API2_EP;

const toAbsolute = (url, base) => {
  try {
    return new URL(url, base || undefined).href;
  } catch (e) {
    return null;
  }
};

const withTrailingSlash = href => (href.endsWith("/") ? href : href + "/");

// El Bearer del huésped sólo viaja a api2 (REACT_APP_API2_EP) y nunca a los
// pedidos que son públicos o pre-sesión: login, info del POS, resolución del
// POS por dominio, magic link y canje del token de acceso. Tampoco a APIOLD ni
// a la guest app (otros hosts).
// Se compara la URL FINAL (resuelta contra baseURL), no el string crudo.
export const shouldAttachToken = (url, baseURL) => {
  const api2 = process.env.REACT_APP_API2_EP;
  if (!api2 || typeof url !== "string") return false;
  const apiBase = toAbsolute(api2);
  if (!apiBase) return false;
  const finalUrl = toAbsolute(url, baseURL || api2);
  if (!finalUrl) return false;
  if (isGuestAppUrl(url) || isGuestAppUrl(finalUrl)) return false;
  if (!finalUrl.startsWith(withTrailingSlash(apiBase))) return false;
  const publicEps = [
    process.env.REACT_APP_LOGIN_EP,
    process.env.REACT_APP_POS_INFO_EP,
    process.env.REACT_APP_RESOLVE_DOMAIN_EP,
    process.env.REACT_APP_MAGIC_LINK,
    process.env.REACT_APP_ACCESS_TOKEN_EXCHANGE_EP
  ];
  return !publicEps.some(ep => {
    if (!ep) return false;
    const abs = toAbsolute(ep, apiBase);
    return abs && finalUrl.startsWith(abs);
  });
};

axios.interceptors.request.use(
  config => {
    if (shouldAttachToken(config.url, config.baseURL)) {
      const token = getToken();
      if (token) config.headers.Authorization = "Bearer " + token;
    }
    return config;
  },
  error => Promise.reject(error)
);

// api2 responde 401 `unauthorized` cuando el token del huésped fue revocado
// (logout / versión de token).
export const isRevokedSession = error =>
  Boolean(
    error &&
      error.response &&
      error.response.status === 401 &&
      error.response.data &&
      error.response.data.code === "unauthorized"
  );

// Add a response interceptor
axios.interceptors.response.use(
  function (response) {
    if (response.data && response.data.response === 0) {
      const message = response.data.message
        ? response.data.message
        : "Error general";
      // Con `code` se lanza un Error que lo conserva; sin él, el string de
      // siempre (varios llamadores lo muestran tal cual en un toast).
      if (!response.data.code) throw message;
      const err = new Error(message);
      err.code = response.data.code;
      err.retry_after = response.data.retry_after;
      err.response = response;
      throw err;
    }
    return response;
  },
  function (error) {
    if (error && error.config && isGuestAppUrl(error.config.url))
      return Promise.reject(error);
    // Los pedidos de login/magic link/canje muestran su propio mensaje según
    // `error.response.data.code`: el interceptor no dispara el toast genérico.
    if (error && error.config && error.config.skipErrorToast)
      return Promise.reject(error);
    if (isRevokedSession(error)) {
      // Token revocado: el contexto limpia la sesión y manda al login.
      window.dispatchEvent(new Event("pxsession:expired"));
      return Promise.reject(error);
    }
    if (error && error.response) {
      // El pedido llegó y el servidor respondió con un status fuera de 2xx.
      // `error.response.data.code` queda intacto en el error que se relanza.
      const data = error.response.data || {};
      if (data.response !== 0)
        toast.error(<FormattedMessage id={`error.${error.response.status}`} />);
      if (data.response === 0) toast.error(data.message);
    }
    // No se loguea el error completo: trae config con headers (Authorization)
    // y el cuerpo del pedido.
    return Promise.reject(error);
  }
);

export default {
  get: axios.get,
  post: axios.post,
  put: axios.put,
  delete: axios.delete,
  apiEndpoint: apiEndpoint
};
